/**
 * 수강권 카탈로그(폼 2-A) 생성 React Query 훅 (instructor-web)
 *
 * 목록 조회는 페이지가 Firestore를 직접 읽는다(`getTicketCatalogList`). 본 모듈은
 * 생성(mutation)만 노출한다. 백엔드 `createTicketCatalog`는 requireAdmin으로
 * 보호되므로 강사가 호출하면 permission-denied로 거부된다(클라에서도 role로 사전 차단).
 */

import { useMutation } from "@tanstack/react-query";
import { httpsCallable } from "firebase/functions";
import { getFirebaseFunctions } from "@/config/firebase";

// --- 백엔드 contract (firebase-functions/src/ticket/create-ticket-catalog.ts) ---

interface CreateTicketCatalogRequest {
  centerName: string;
  title: string;
  type: "P" | "G";
  totalCoupon: number;
  validDays?: number;
  maxTrainee?: number;
}

interface CreateTicketCatalogResponse {
  success: true;
  /** UUID v4 */
  ticketId: string;
}

export interface CreateTicketCatalogParams {
  centerName: string;
  title: string;
  type: "P" | "G";
  totalCoupon: number;
  validDays?: number;
  maxTrainee?: number;
}

/**
 * 수강권 카탈로그 생성 mutation.
 *
 * 성공 시 서버에서 발급된 ticketId를 반환한다. 목록 갱신은 호출자(페이지)가
 * onSuccess 콜백에서 직접 refetch한다.
 */
export function useCreateTicketCatalog() {
  return useMutation({
    mutationFn: async (params: CreateTicketCatalogParams) => {
      const fn = httpsCallable<CreateTicketCatalogRequest, CreateTicketCatalogResponse>(
        getFirebaseFunctions(),
        "createTicketCatalog",
      );
      const result = await fn(params);
      return result.data;
    },
  });
}

// --- 수정(강사 가능) / 삭제(owner·admin 전용) contract ---

interface UpdateTicketCatalogRequest {
  centerId: number;
  ticketId: string;
  title?: string;
  type?: "P" | "G";
  totalCoupon?: number;
  /** number=설정, null=제거(무제한), undefined=유지 */
  validDays?: number | null;
  /** number=설정, null=제거(미지정), undefined=유지 */
  maxTrainee?: number | null;
}

interface DeleteTicketCatalogRequest {
  centerId: number;
  ticketId: string;
}

interface TicketCatalogMutationResponse {
  success: true;
}

export type UpdateTicketCatalogParams = UpdateTicketCatalogRequest;
export type DeleteTicketCatalogParams = DeleteTicketCatalogRequest;

/**
 * 수강권 카탈로그 수정 mutation. 강사 전용 `updateInstructorTicketCatalog`(requireAuth +
 * 센터 멤버십)를 호출하므로 강사도 호출 가능하다.
 */
export function useUpdateTicketCatalog() {
  return useMutation({
    mutationFn: async (params: UpdateTicketCatalogParams) => {
      const fn = httpsCallable<UpdateTicketCatalogRequest, TicketCatalogMutationResponse>(
        getFirebaseFunctions(),
        "updateInstructorTicketCatalog",
      );
      const result = await fn(params);
      return result.data;
    },
  });
}

/**
 * 수강권 카탈로그 삭제(소프트) mutation. `deleteTicketCatalog`(requireAdmin)를 호출하므로
 * owner/admin만 성공한다. 호출 전 페이지에서 role로 분기한다.
 */
export function useDeleteTicketCatalog() {
  return useMutation({
    mutationFn: async (params: DeleteTicketCatalogParams) => {
      const fn = httpsCallable<DeleteTicketCatalogRequest, TicketCatalogMutationResponse>(
        getFirebaseFunctions(),
        "deleteTicketCatalog",
      );
      const result = await fn(params);
      return result.data;
    },
  });
}
